import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { IconButton, ListRow, Status } from '@elbklar/ui';
import type { Task } from '@/lib/api';

/** Was eine Aufgabe für die Zeile braucht; Aufgaben-Liste und Übersicht liefern verschieden viel. */
export type AufgabeKern = Pick<Task, 'id' | 'title' | 'description' | 'due_date'> & { priority: string; status: string };

interface TaskCardProps<T extends AufgabeKern> {
  task: T;
  /** Erledigen; solange das Promise läuft, steht der Haken schon. */
  onStatusChange?: (taskId: string, status: string) => Promise<void> | void;
  onEdit?: (task: T) => void;
}

// Nur was auffallen soll: „Mittel“ und „Niedrig“ stehen nicht an jeder
// Aufgabe. Wort und Ton aus STATUS (`priority_…`).
const AUFFALLEND = new Set(['high', 'urgent']);

/**
 * Eine Aufgabe in einer `Card flush`: ListRow aus @elbklar/ui, dieselbe Zeile
 * wie die Aufgaben-Karte im PM. Runde Checkbox zum Erledigen (auf Touch 44 px
 * Trefferfläche), Fälligkeit, Beschreibung, rechts Bearbeiten.
 */
export function TaskCard<T extends AufgabeKern>({ task, onStatusChange, onEdit }: TaskCardProps<T>) {
  const [laeuft, setLaeuft] = useState(false);
  const abgeschlossen = task.status === 'completed' || task.status === 'cancelled';
  const prioritaet = AUFFALLEND.has(task.priority);

  const erledigen = async (an: boolean) => {
    if (!an || !onStatusChange || abgeschlossen) return;
    setLaeuft(true);
    try {
      await onStatusChange(task.id, 'completed');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <ListRow
      checkable
      checked={abgeschlossen || laeuft}
      // Erledigtes lässt sich hier nicht zurückholen, nur im Bearbeiten.
      disabled={abgeschlossen || laeuft || !onStatusChange}
      onCheckedChange={erledigen}
      title={task.title}
      description={task.description}
      faellig={task.due_date}
      meta={
        (prioritaet || task.status !== 'open') && (
          <>
            {prioritaet && !abgeschlossen && <Status status={`priority_${task.priority}`} variant="plain" />}
            {task.status !== 'open' && <Status status={`task_${task.status}`} variant="plain" />}
          </>
        )
      }
      trailing={onEdit && <IconButton icon={Pencil} label="Aufgabe bearbeiten" variant="ghost" size="sm" onClick={() => onEdit(task)} />}
    />
  );
}
